export default {
  en: {
    title: 'Forensic audit',
    subtitle: 'Rebuilds a queue next to the state of every agent of its segment, second by second.',
    search: {
      placeholder: 'Queue ID',
      action: 'Audit',
      hint: 'Enter the ID of a queue to see its timeline. You can copy it from the queue history.',
      invalid: 'The queue ID must have 24 hexadecimal characters.',
    },
    errors: {
      notFound: 'The queue was not found in this account.',
      forbidden: 'Only administrators can run a forensic audit.',
      generic: 'The audit could not be loaded, please try again.',
    },
    summary: {
      title: 'Queue',
      contextTitle: 'ACD configuration',
      segment: 'Segment',
      startedAt: 'Entered',
      waited: 'Waited',
      rings: 'Rings',
      outcome: 'Result',
      owner: 'Fixed agent',
      contactAgents: 'Agents linked to the contact',
      ringTimeout: 'Ring time',
      acd: 'ACD',
      affinity: 'Contact affinity',
      affinityOff: 'disabled',
      affinityUnlimited: 'no attempt limit',
      affinityLimit: 'released after {count} unanswered',
      none: 'None',
      seconds: '{count} s',
      configNote: 'The ACD configuration is the current one of the account, not necessarily the one in force when the queue was open.',
    },
    outcomes: {
      answered: 'Answered',
      abandoned: 'Abandoned by the customer',
      dismissed: 'Dismissed',
      waiting: 'Waiting',
    },
    acd: {
      circular: 'Circular',
      unique: 'Unique',
      none: 'Not set',
    },
    states: {
      available: 'Available',
      ringing: 'Ringing',
      'in-call': 'In call',
      meeting: 'In meeting',
      busy: 'Busy',
      disconnected: 'Disconnected',
      unknown: 'Unknown',
    },
    lanes: {
      title: 'Timeline',
      hint: 'Click on the timeline to see the state of every agent at that instant.',
      queue: 'Queue',
      notInSegment: 'Not in the segment',
      initial: {
        log: 'Initial state from the last connection log',
        snapshot: 'Initial state from the snapshot saved with the queue',
        none: 'Without records before the queue, the initial state is unknown',
      },
      ringTooltip: 'Ring to {agent}: {outcome}',
      segmentTooltip: '{state} ({value})',
    },
    cursor: {
      title: 'State at {time}',
      clear: 'Clear',
      since: 'since {time}',
      ringing: 'Ringing to {agent}',
      noRing: 'No ring at this instant',
    },
    rings: {
      title: 'ACD rings',
      empty: 'The ACD never rang an agent for this queue.',
      agent: 'Agent',
      time: 'Time',
      result: 'Result',
      available: 'Others available at that moment',
      nobody: 'Nobody else was available',
      outcomes: {
        timeout: 'No answer',
        answered: 'Answered',
        dismissed: 'Dismissed',
        pending: 'Ringing',
      },
    },
    events: {
      title: 'Events',
      source: 'Source',
      allSources: 'All sources',
      allAgents: 'All agents',
      time: 'Time',
      agent: 'Agent',
      event: 'Event',
      detail: 'Detail',
      sources: {
        queue: 'Queue',
        customer: 'Customer',
        agent: 'Agent',
      },
      kinds: {
        entered: 'Customer entered the queue',
        assigned: 'Assigned',
        timeout: 'Ring without answer',
        answered: 'Answered',
        dismissed: 'Dismissed by the agent',
        dismiss: 'Dismissed by the agent',
        connected: 'Customer connected',
        disconnected: 'Customer disconnected',
      },
      types: {
        'status-changed': 'Status',
        'status-changed-user': 'Status chosen by the agent',
        acd: 'ACD',
        'network-issues': 'Network',
        login: 'Login',
        logout: 'Logout',
      },
    },
  },
  es: {
    title: 'Auditoría forense',
    subtitle: 'Reconstruye una fila junto al estado de cada ejecutivo de su segmento, segundo a segundo.',
    search: {
      placeholder: 'ID de la fila',
      action: 'Auditar',
      hint: 'Ingresa el ID de una fila para ver su línea de tiempo. Puedes copiarlo desde el historial de filas.',
      invalid: 'El ID de la fila debe tener 24 caracteres hexadecimales.',
    },
    errors: {
      notFound: 'La fila no existe en esta cuenta.',
      forbidden: 'Solo los administradores pueden ejecutar una auditoría forense.',
      generic: 'No se pudo cargar la auditoría, intenta nuevamente.',
    },
    summary: {
      title: 'Fila',
      contextTitle: 'Configuración del ACD',
      segment: 'Segmento',
      startedAt: 'Ingreso',
      waited: 'Tiempo de espera',
      rings: 'Timbres',
      outcome: 'Resultado',
      owner: 'Ejecutivo fijo',
      contactAgents: 'Ejecutivos asociados al contacto',
      ringTimeout: 'Tiempo de timbre',
      acd: 'ACD',
      affinity: 'Afinidad por contacto',
      affinityOff: 'desactivada',
      affinityUnlimited: 'sin límite de intentos',
      affinityLimit: 'se libera tras {count} sin respuesta',
      none: 'Ninguno',
      seconds: '{count} s',
      configNote: 'La configuración del ACD es la actual de la cuenta, no necesariamente la vigente cuando la fila estuvo abierta.',
    },
    outcomes: {
      answered: 'Atendida',
      abandoned: 'Abandonada por el cliente',
      dismissed: 'Descartada',
      waiting: 'En espera',
    },
    acd: {
      circular: 'Circular',
      unique: 'Único',
      none: 'Sin configurar',
    },
    states: {
      available: 'Disponible',
      ringing: 'Timbrando',
      'in-call': 'En llamada',
      meeting: 'En reunión',
      busy: 'Ocupado',
      disconnected: 'Desconectado',
      unknown: 'Desconocido',
    },
    lanes: {
      title: 'Línea de tiempo',
      hint: 'Haz clic en la línea de tiempo para ver el estado de cada ejecutivo en ese instante.',
      queue: 'Fila',
      notInSegment: 'Fuera del segmento',
      initial: {
        log: 'Estado inicial según el último registro de conexión',
        snapshot: 'Estado inicial según la foto guardada con la fila',
        none: 'Sin registros previos a la fila, el estado inicial se desconoce',
      },
      ringTooltip: 'Timbre a {agent}: {outcome}',
      segmentTooltip: '{state} ({value})',
    },
    cursor: {
      title: 'Estado a las {time}',
      clear: 'Limpiar',
      since: 'desde las {time}',
      ringing: 'Timbrando a {agent}',
      noRing: 'Sin timbre en este instante',
    },
    rings: {
      title: 'Timbres del ACD',
      empty: 'El ACD nunca timbró a un ejecutivo para esta fila.',
      agent: 'Ejecutivo',
      time: 'Hora',
      result: 'Resultado',
      available: 'Otros disponibles en ese momento',
      nobody: 'No había nadie más disponible',
      outcomes: {
        timeout: 'Sin respuesta',
        answered: 'Atendida',
        dismissed: 'Descartada',
        pending: 'Timbrando',
      },
    },
    events: {
      title: 'Eventos',
      source: 'Origen',
      allSources: 'Todos los orígenes',
      allAgents: 'Todos los ejecutivos',
      time: 'Hora',
      agent: 'Ejecutivo',
      event: 'Evento',
      detail: 'Detalle',
      sources: {
        queue: 'Fila',
        customer: 'Cliente',
        agent: 'Ejecutivo',
      },
      kinds: {
        entered: 'El cliente ingresó a la fila',
        assigned: 'Asignada',
        timeout: 'Timbre sin respuesta',
        answered: 'Atendida',
        dismissed: 'Descartada por el ejecutivo',
        dismiss: 'Descartada por el ejecutivo',
        connected: 'Cliente conectado',
        disconnected: 'Cliente desconectado',
      },
      types: {
        'status-changed': 'Estado',
        'status-changed-user': 'Estado elegido por el ejecutivo',
        acd: 'ACD',
        'network-issues': 'Red',
        login: 'Inicio de sesión',
        logout: 'Cierre de sesión',
      },
    },
  },
  fr: {
    title: 'Audit forensique',
    subtitle: 'Reconstitue une file avec l\'état de chaque agent de son segment, seconde par seconde.',
    search: {
      placeholder: 'ID de la file',
      action: 'Auditer',
      hint: 'Saisissez l\'ID d\'une file pour voir sa chronologie. Vous pouvez le copier depuis l\'historique des files.',
      invalid: 'L\'ID de la file doit contenir 24 caractères hexadécimaux.',
    },
    errors: {
      notFound: 'La file est introuvable dans ce compte.',
      forbidden: 'Seuls les administrateurs peuvent lancer un audit forensique.',
      generic: 'Impossible de charger l\'audit, veuillez réessayer.',
    },
    summary: {
      title: 'File',
      contextTitle: 'Configuration de l\'ACD',
      segment: 'Segment',
      startedAt: 'Entrée',
      waited: 'Temps d\'attente',
      rings: 'Sonneries',
      outcome: 'Résultat',
      owner: 'Agent fixe',
      contactAgents: 'Agents liés au contact',
      ringTimeout: 'Durée de sonnerie',
      acd: 'ACD',
      affinity: 'Affinité par contact',
      affinityOff: 'désactivée',
      affinityUnlimited: 'sans limite de tentatives',
      affinityLimit: 'libérée après {count} sans réponse',
      none: 'Aucun',
      seconds: '{count} s',
      configNote: 'La configuration de l\'ACD est l\'actuelle du compte, pas forcément celle en vigueur lorsque la file était ouverte.',
    },
    outcomes: {
      answered: 'Répondue',
      abandoned: 'Abandonnée par le client',
      dismissed: 'Rejetée',
      waiting: 'En attente',
    },
    acd: {
      circular: 'Circulaire',
      unique: 'Unique',
      none: 'Non configuré',
    },
    states: {
      available: 'Disponible',
      ringing: 'En sonnerie',
      'in-call': 'En appel',
      meeting: 'En réunion',
      busy: 'Occupé',
      disconnected: 'Déconnecté',
      unknown: 'Inconnu',
    },
    lanes: {
      title: 'Chronologie',
      hint: 'Cliquez sur la chronologie pour voir l\'état de chaque agent à cet instant.',
      queue: 'File',
      notInSegment: 'Hors du segment',
      initial: {
        log: 'État initial d\'après le dernier journal de connexion',
        snapshot: 'État initial d\'après l\'instantané enregistré avec la file',
        none: 'Sans journal antérieur à la file, l\'état initial est inconnu',
      },
      ringTooltip: 'Sonnerie vers {agent} : {outcome}',
      segmentTooltip: '{state} ({value})',
    },
    cursor: {
      title: 'État à {time}',
      clear: 'Effacer',
      since: 'depuis {time}',
      ringing: 'Sonnerie vers {agent}',
      noRing: 'Aucune sonnerie à cet instant',
    },
    rings: {
      title: 'Sonneries de l\'ACD',
      empty: 'L\'ACD n\'a jamais fait sonner un agent pour cette file.',
      agent: 'Agent',
      time: 'Heure',
      result: 'Résultat',
      available: 'Autres disponibles à ce moment',
      nobody: 'Personne d\'autre n\'était disponible',
      outcomes: {
        timeout: 'Sans réponse',
        answered: 'Répondue',
        dismissed: 'Rejetée',
        pending: 'En sonnerie',
      },
    },
    events: {
      title: 'Événements',
      source: 'Origine',
      allSources: 'Toutes les origines',
      allAgents: 'Tous les agents',
      time: 'Heure',
      agent: 'Agent',
      event: 'Événement',
      detail: 'Détail',
      sources: {
        queue: 'File',
        customer: 'Client',
        agent: 'Agent',
      },
      kinds: {
        entered: 'Le client est entré dans la file',
        assigned: 'Assignée',
        timeout: 'Sonnerie sans réponse',
        answered: 'Répondue',
        dismissed: 'Rejetée par l\'agent',
        dismiss: 'Rejetée par l\'agent',
        connected: 'Client connecté',
        disconnected: 'Client déconnecté',
      },
      types: {
        'status-changed': 'Statut',
        'status-changed-user': 'Statut choisi par l\'agent',
        acd: 'ACD',
        'network-issues': 'Réseau',
        login: 'Connexion',
        logout: 'Déconnexion',
      },
    },
  },
};
